import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT Bearer token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Refresh-aware 401 interceptor
const SKIP_REFRESH_URLS = ['/api/v1/auth/refresh', '/api/v1/auth/me'];
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  failedQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      SKIP_REFRESH_URLS.some((url) => originalRequest.url?.includes(url))
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      isRefreshing = false;
      processQueue(error, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/refresh`,
        null,
        { params: { refresh_token: refreshToken } },
      );
      const { access_token, refresh_token: newRefresh } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', newRefresh);

      processQueue(null, access_token);
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─── Auth ───

export const authAPI = {
  register: (data: { email: string; password: string; display_name?: string; analysis_id?: number; invite_token?: string }) =>
    api.post('/api/v1/auth/register', data),
  login: (data: { email: string; password: string; analysis_id?: number }) =>
    api.post('/api/v1/auth/login', data),
  forgotPassword: (email: string) =>
    api.post('/api/v1/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token: string, new_password: string) =>
    api.post('/api/v1/auth/reset-password', null, { params: { token, new_password } }),
  verifyEmail: (token: string) =>
    api.get('/api/v1/auth/verify-email', { params: { token } }),
  sendVerification: (email: string) =>
    api.post('/api/v1/auth/send-verification', null, { params: { email } }),
};

// ─── Organizations ───

export const orgsAPI = {
  list: () => api.get('/api/v1/orgs/'),
  create: (data: { name: string; slug?: string }) =>
    api.post('/api/v1/orgs/', data),
  get: (orgId: number) => api.get(`/api/v1/orgs/${orgId}`),
  update: (orgId: number, data: { name?: string }) =>
    api.put(`/api/v1/orgs/${orgId}`, data),
  members: (orgId: number) => api.get(`/api/v1/orgs/${orgId}/members`),
  invite: (orgId: number, data: { email: string; role?: string }) =>
    api.post(`/api/v1/orgs/${orgId}/invite`, data),
  changeRole: (orgId: number, userId: number, role: string) =>
    api.put(`/api/v1/orgs/${orgId}/members/${userId}`, null, { params: { role } }),
  removeMember: (orgId: number, userId: number) =>
    api.delete(`/api/v1/orgs/${orgId}/members/${userId}`),
  switchOrg: (orgId: number) => api.post(`/api/v1/orgs/switch/${orgId}`),
  acceptInvite: (token: string) =>
    api.post('/api/v1/orgs/accept-invite', null, { params: { token } }),
  apiKeys: (orgId: number) => api.get(`/api/v1/orgs/${orgId}/api-keys`),
  createApiKey: (orgId: number, data: { name: string }) =>
    api.post(`/api/v1/orgs/${orgId}/api-keys`, data),
  deleteApiKey: (orgId: number, keyId: number) =>
    api.delete(`/api/v1/orgs/${orgId}/api-keys/${keyId}`),
};

// ─── Sites ───

export const sitesAPI = {
  list: (params?: { search?: string; limit?: number; offset?: number; include_inactive?: boolean }) =>
    api.get('/api/v1/sites/', { params }),
  create: (data: { name: string; domain: string; repo_path: string; vertical?: string }) =>
    api.post('/api/v1/sites/', data),
  get: (id: number) => api.get(`/api/v1/sites/${id}`),
  update: (id: number, data: Record<string, any>) => api.put(`/api/v1/sites/${id}`, data),
  delete: (id: number) => api.delete(`/api/v1/sites/${id}`),
  importFromAnalysis: (analysisId: number) =>
    api.post('/api/v1/sites/from-analysis', { analysis_id: analysisId }),
};

// ─── Audit ───

export const auditAPI = {
  run: (siteId: number) => api.post(`/api/v1/audit/run/${siteId}`),
  runAll: () => api.post('/api/v1/audit/run-all'),
  results: (siteId: number) => api.get(`/api/v1/audit/results/${siteId}`),
};

// ─── Directives ───

export const directivesAPI = {
  list: (params?: { status?: string; site_id?: number }) =>
    api.get('/api/v1/directives/', { params }),
  approve: (directiveIds: number[]) =>
    api.post('/api/v1/directives/approve', { directive_ids: directiveIds }),
  reject: (id: number) => api.post(`/api/v1/directives/reject/${id}`),
  preview: (id: number) => api.get(`/api/v1/directives/${id}/preview`),
  fixIt: (id: number) => api.post(`/api/v1/directives/${id}/fix-it`),
  diy: (id: number) => api.post(`/api/v1/directives/${id}/diy`),
};

// ─── Tokens ───

export const tokensAPI = {
  balance: () => api.get('/api/v1/tokens/balance'),
  history: (params?: { limit?: number; offset?: number }) =>
    api.get('/api/v1/tokens/history', { params }),
  pricing: () => api.get('/api/v1/tokens/pricing'),
  purchase: (data: { pack_size?: number; success_url?: string; cancel_url?: string }) =>
    api.post('/api/v1/tokens/purchase', data),
  refund: (transactionId: number) =>
    api.post(`/api/v1/tokens/${transactionId}/refund`),
};

// ─── Hosting ───

export const hostingAPI = {
  status: (siteId: number) => api.get(`/api/v1/hosting/${siteId}/status`),
  setupManaged: (siteId: number, data?: { max_depth?: number }) =>
    api.post(`/api/v1/hosting/${siteId}/setup-managed`, data || {}),
  redeploy: (siteId: number) => api.post(`/api/v1/hosting/${siteId}/redeploy`),
  setupDomain: (siteId: number, domain: string) =>
    api.post(`/api/v1/hosting/${siteId}/custom-domain`, { domain }),
  domainStatus: (siteId: number) => api.get(`/api/v1/hosting/${siteId}/domain-status`),
  removeDomain: (siteId: number) => api.delete(`/api/v1/hosting/${siteId}/custom-domain`),
};

// ─── Deploy ───

export const deployAPI = {
  run: (siteId: number) => api.post(`/api/v1/deploy/run/${siteId}`),
  rollback: (directiveId: number) => api.post(`/api/v1/deploy/rollback/${directiveId}`),
  runAll: () => api.post('/api/v1/deploy/run-all'),
};

// ─── Pipeline ───

export const pipelineAPI = {
  ingest: () => api.post('/api/v1/pipeline/ingest'),
  analyze: () => api.post('/api/v1/pipeline/analyze'),
  strategize: () => api.post('/api/v1/pipeline/strategize'),
  measure: () => api.post('/api/v1/pipeline/measure'),
  full: () => api.post('/api/v1/pipeline/full'),
  signals: (params?: { signal_type?: string; min_confidence?: number; limit?: number }) =>
    api.get('/api/v1/pipeline/signals', { params }),
  sources: () => api.get('/api/v1/pipeline/sources'),
  createSource: (data: { name: string; url: string; source_type?: string; category?: string; fetch_interval_hours?: number }) =>
    api.post('/api/v1/pipeline/sources', data),
  chunks: (params?: { is_processed?: boolean; source_id?: number; limit?: number }) =>
    api.get('/api/v1/pipeline/chunks', { params }),
};

// ─── Measure ───

export const measureAPI = {
  run: (siteId: number, params?: { deployment_id?: number; validate_live?: boolean }) =>
    api.post(`/api/v1/measure/run/${siteId}`, null, { params }),
  runAll: (params?: { validate_live?: boolean }) =>
    api.post('/api/v1/measure/run-all', null, { params }),
  history: (siteId: number, params?: { limit?: number }) =>
    api.get(`/api/v1/measure/history/${siteId}`, { params }),
  latest: (siteId: number) => api.get(`/api/v1/measure/latest/${siteId}`),
};

// ─── Health ───

export const healthAPI = {
  check: () => api.get('/health'),
};

// ─── Mission Control ───

export const missionControlAPI = {
  overview: () => api.get('/api/v1/mission-control/overview'),
};

// ─── Agents ───

export const agentsAPI = {
  list: (params?: { site_id?: number; agent_type?: string }) =>
    api.get('/api/v1/agents/', { params }),
  get: (id: number) => api.get(`/api/v1/agents/${id}`),
  seed: () => api.post('/api/v1/agents/seed'),
  updateStatus: (id: number, data: { status: string; current_task_summary?: string; current_session_id?: string }) =>
    api.put(`/api/v1/agents/${id}/status`, data),
  activities: (id: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/api/v1/agents/${id}/activities`, { params }),
  logActivity: (id: number, data: { request_summary: string; session_id?: string; source?: string }) =>
    api.post(`/api/v1/agents/${id}/activities`, data),
  updateActivity: (activityId: number, data: { result_summary?: string; status?: string }) =>
    api.put(`/api/v1/agents/activities/${activityId}`, data),
  launch: (id: number) => api.post(`/api/v1/agents/${id}/launch`),
};

// ─── Releases ───

export const releasesAPI = {
  list: (params?: { site_id?: number }) =>
    api.get('/api/v1/releases/', { params }),
  create: (data: any) => api.post('/api/v1/releases/', data),
  get: (id: number) => api.get(`/api/v1/releases/${id}`),
  update: (id: number, data: any) => api.put(`/api/v1/releases/${id}`, data),
  pending: () => api.get('/api/v1/releases/pending'),
};

// ─── Backlog ───

export const backlogAPI = {
  list: (params?: { site_id?: number; category?: string; status?: string }) =>
    api.get('/api/v1/backlog/', { params }),
  create: (data: any) => api.post('/api/v1/backlog/', data),
  update: (id: number, data: any) => api.put(`/api/v1/backlog/${id}`, data),
  delete: (id: number) => api.delete(`/api/v1/backlog/${id}`),
  toggleCheck: (id: number) => api.put(`/api/v1/backlog/${id}/check`),
  route: (id: number) => api.post(`/api/v1/backlog/${id}/route`),
  pipeline: () => api.get('/api/v1/backlog/pipeline'),
  reorder: (data: { items: Array<{ id: number; sort_order: number }> }) =>
    api.put('/api/v1/backlog/reorder', data),
  syncGithub: (siteId: number) => api.post(`/api/v1/backlog/sync-github/${siteId}`),
};


// ─── Analyzer ───

export const analyzerAPI = {
  scan: (data: {
    url: string;
    email?: string;
    notes?: string;
    ownership_confirmed?: boolean;
    terms_accepted_at?: string;
  }) => api.post('/api/v1/analyzer/scan', data),
  get: (analysisId: number) => api.get(`/api/v1/analyzer/${analysisId}`),
  scorecard: (analysisId: number) => api.get(`/api/v1/analyzer/${analysisId}/scorecard`),
  recent: (params?: { limit?: number }) =>
    api.get('/api/v1/analyzer/recent/list', { params }),
  myCompleted: (params?: { limit?: number }) =>
    api.get('/api/v1/analyzer/my/completed', { params }),
  streamUrl: (analysisId: number): string =>
    `${API_BASE_URL}/api/v1/analyzer/${analysisId}/stream`,
  emailReport: (analysisId: number) =>
    api.post(`/api/v1/analyzer/${analysisId}/email-report`),
  share: (analysisId: number) =>
    api.post(`/api/v1/analyzer/${analysisId}/share`),
  publicReport: (shareToken: string) =>
    api.get(`/api/v1/public/report/${shareToken}`),
};

// ─── Demos ───

export const demosAPI = {
  list: (params?: { status?: string; limit?: number }) =>
    api.get('/api/v1/demos/', { params }),
  get: (demoId: number) => api.get(`/api/v1/demos/${demoId}`),
  getBySlug: (slug: string) => api.get(`/api/v1/demos/by-slug/${slug}`),
  convert: (demoId: number) => api.post(`/api/v1/demos/${demoId}/convert`),
};


// ─── Guardrails ───

export const guardrailsAPI = {
  list: (params?: { site_id?: number }) =>
    api.get('/api/v1/guardrails/', { params }),
  create: (data: { site_id: number; rule_type: string; rule_name: string; description?: string; validation_prompt?: string; severity?: string }) =>
    api.post('/api/v1/guardrails/', data),
  update: (id: number, data: { rule_name?: string; description?: string; severity?: string; is_active?: boolean }) =>
    api.put(`/api/v1/guardrails/${id}`, data),
  delete: (id: number) => api.delete(`/api/v1/guardrails/${id}`),
  interview: (siteId: number) => api.post(`/api/v1/guardrails/interview/${siteId}`),
  seed: (siteId: number, vertical?: string) =>
    api.post(`/api/v1/guardrails/seed/${siteId}`, null, { params: { vertical } }),
  validate: (data: { content: string; site_id: number; content_ref?: string }) =>
    api.post('/api/v1/guardrails/validate', data),
};

// ─── Feedback ───

export const feedbackAPI = {
  submit: (data: { site_id: number; demo_site_id?: number; feedback_text: string; page_path?: string; source?: string; email?: string }) =>
    api.post('/api/v1/feedback/', data),
  list: (params?: { site_id?: number; status?: string; limit?: number }) =>
    api.get('/api/v1/feedback/', { params }),
  get: (id: number) => api.get(`/api/v1/feedback/${id}`),
  clarify: (id: number, response: string) =>
    api.post(`/api/v1/feedback/${id}/clarify`, { response }),
};

// ─── Content Editing ───

export const contentAPI = {
  submitEdit: (data: { site_id: number; demo_site_id?: number; instruction: string; target_page?: string }) =>
    api.post('/api/v1/content/edit', data),
  submitEditNow: (data: { site_id: number; demo_site_id?: number; instruction: string; target_page?: string }) =>
    api.post('/api/v1/content/edit-now', data, { timeout: 60000 }),
  listEdits: (params?: { site_id?: number; status?: string; limit?: number }) =>
    api.get('/api/v1/content/edits', { params }),
  getEdit: (id: number) => api.get(`/api/v1/content/edits/${id}`),
  revertEdit: (id: number) => api.post(`/api/v1/content/edits/${id}/revert`),
};

// ─── Chat ───

export const chatAPI = {
  message: (siteSlug: string, data: { message: string; mode?: string; page_path?: string; email?: string; session_id?: string }) =>
    api.post(`/api/v1/chat/${siteSlug}/message`, data),
};

// ─── Scorecard Chat ───

export const scorecardChatAPI = {
  message: (analysisId: number, data: { message: string; category_name?: string }) =>
    api.post(`/api/v1/scorecard-chat/${analysisId}/message`, data),
};

// ─── GEO Rules Engine ───

export const geoRulesAPI = {
  list: (params?: { rule_type?: string; status?: string }) =>
    api.get('/api/v1/geo-rules/', { params }),
  get: (ruleId: number) =>
    api.get(`/api/v1/geo-rules/${ruleId}`),
  create: (data: { rule_name: string; rule_type: string; description?: string; condition: Record<string, any>; action: Record<string, any>; priority?: number; applies_to_verticals?: string[]; applies_to_pages?: string; is_auto_apply?: boolean }) =>
    api.post('/api/v1/geo-rules/', data),
  update: (ruleId: number, data: Record<string, any>) =>
    api.put(`/api/v1/geo-rules/${ruleId}`, data),
  delete: (ruleId: number) =>
    api.delete(`/api/v1/geo-rules/${ruleId}`),
  stats: () =>
    api.get('/api/v1/geo-rules/stats'),
  seed: () =>
    api.post('/api/v1/geo-rules/seed'),
  evaluateSite: (siteId: number) =>
    api.post(`/api/v1/geo-rules/evaluate/${siteId}`),
  evaluateAll: () =>
    api.post('/api/v1/geo-rules/evaluate-all'),
};

// ─── Billing ───

export const billingAPI = {
  getSubscription: () => api.get('/api/v1/billing/subscription'),
  createCheckout: (tier: string) =>
    api.post('/api/v1/billing/checkout', null, { params: { tier } }),
  createPortal: () => api.post('/api/v1/billing/portal'),
};

// ─── Admin Platform ───

export const adminPlatformAPI = {
  // RBAC
  getRoles: () => api.get('/api/v1/rbac/roles'),
  createRole: (data: { role_name: string; description?: string; permissions?: Record<string, boolean> }) =>
    api.post('/api/v1/rbac/roles', data),
  updateRole: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/rbac/roles/${id}`, data),
  deleteRole: (id: number) => api.delete(`/api/v1/rbac/roles/${id}`),
  getPermissions: () => api.get('/api/v1/rbac/permissions'),

  // Audit log
  getAuditLog: (params?: { action?: string; actor?: string; from_date?: string; to_date?: string; limit?: number; offset?: number }) =>
    api.get('/api/v1/audit-log/', { params }),
  exportAuditLog: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/api/v1/audit-log/export', { params, responseType: 'blob' }),

  // Admin dashboard
  getStats: (params?: { site_id?: number }) =>
    api.get('/api/v1/admin-dashboard/stats', { params }),
  getSites: () => api.get('/api/v1/admin-dashboard/sites'),
  updateSiteConfig: (siteId: number, data: Record<string, any>) =>
    api.put(`/api/v1/admin-dashboard/sites/${siteId}/config`, data),

  // Feedback admin
  updateFeedbackStatus: (id: number, status: string, admin_notes?: string, resolution_summary?: string) =>
    api.patch(`/api/v1/feedback-admin/${id}/status`, { status, admin_notes, resolution_summary }),
  triageFeedback: (id: number) => api.post(`/api/v1/feedback-admin/${id}/triage`),
  dispatchFeedback: (id: number) => api.post(`/api/v1/feedback-admin/${id}/dispatch`),
  feedbackFixStatus: (id: number) => api.get(`/api/v1/feedback-admin/${id}/fix-status`),
  getFeedbackStats: (params?: { site_id?: number }) =>
    api.get('/api/v1/feedback-admin/stats', { params }),

  // Knowledge base
  getKBArticles: (params?: { site_id?: number; category?: string }) =>
    api.get('/api/v1/knowledge-base/articles', { params }),
  createKBArticle: (data: Record<string, any>) =>
    api.post('/api/v1/knowledge-base/articles', data),
  updateKBArticle: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/knowledge-base/articles/${id}`, data),
  deleteKBArticle: (id: number) => api.delete(`/api/v1/knowledge-base/articles/${id}`),
  getKBCategories: (params?: { site_id?: number }) =>
    api.get('/api/v1/knowledge-base/categories', { params }),

  // Announcements
  getAnnouncements: (params?: { site_id?: number }) =>
    api.get('/api/v1/announcements/', { params }),
  createAnnouncement: (data: Record<string, any>) =>
    api.post('/api/v1/announcements/', data),
  updateAnnouncement: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/announcements/${id}`, data),
  deleteAnnouncement: (id: number) => api.delete(`/api/v1/announcements/${id}`),

  // Impersonation
  startImpersonation: (data: { site_id: number; target_user_identifier: string; target_user_display?: string; reason?: string }) =>
    api.post('/api/v1/impersonation/start', data),
  endImpersonation: () => api.post('/api/v1/impersonation/end'),
  getActiveImpersonation: () => api.get('/api/v1/impersonation/active'),
  getImpersonationHistory: (params?: { site_id?: number; limit?: number }) =>
    api.get('/api/v1/impersonation/history', { params }),

  // Chat admin
  getChatConfig: (params?: { site_id?: number }) =>
    api.get('/api/v1/chat-admin/config', { params }),
  updateChatConfig: (data: Record<string, any>) =>
    api.put('/api/v1/chat-admin/config', data),
  getChatSessions: (params?: { site_id?: number; limit?: number }) =>
    api.get('/api/v1/chat-admin/sessions', { params }),
  getChatSession: (id: number) => api.get(`/api/v1/chat-admin/sessions/${id}`),

  // Webhooks
  getWebhooks: (params?: { site_id?: number }) =>
    api.get('/api/v1/webhooks/', { params }),
  createWebhook: (data: Record<string, any>) =>
    api.post('/api/v1/webhooks/', data),
  updateWebhook: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/webhooks/${id}`, data),
  deleteWebhook: (id: number) => api.delete(`/api/v1/webhooks/${id}`),
  getWebhookDeliveries: (id: number) => api.get(`/api/v1/webhooks/${id}/deliveries`),
  testWebhook: (id: number) => api.post(`/api/v1/webhooks/${id}/test`),

  // Feature toggles
  getFeatureToggles: (params?: { site_id?: number }) =>
    api.get('/api/v1/feature-toggles/', { params }),
  createFeatureToggle: (data: Record<string, any>) =>
    api.post('/api/v1/feature-toggles/', data),
  updateFeatureToggle: (id: number, data: Record<string, any>) =>
    api.put(`/api/v1/feature-toggles/${id}`, data),
  deleteFeatureToggle: (id: number) => api.delete(`/api/v1/feature-toggles/${id}`),
  toggleFeatureToggle: (id: number) => api.post(`/api/v1/feature-toggles/${id}/toggle`),

  // Analytics
  getAnalyticsOverview: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/overview', { params }),
  getFeedbackTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/feedback-trends', { params }),
  getChatTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/chat-trends', { params }),
  getGuardrailTrends: (params?: { site_id?: number; period?: string }) =>
    api.get('/api/v1/analytics/guardrail-trends', { params }),

  // Design sessions
  startDesignSession: (feedbackId: number) =>
    api.post(`/api/v1/design-sessions/start`, { feedback_id: feedbackId }),
  sendDesignMessage: (sessionId: number, message: string) =>
    api.post(`/api/v1/design-sessions/${sessionId}/message`, { message }),
  acceptDesignSession: (sessionId: number) =>
    api.post(`/api/v1/design-sessions/${sessionId}/accept`),
  abandonDesignSession: (sessionId: number) =>
    api.post(`/api/v1/design-sessions/${sessionId}/abandon`),
  getDesignSession: (sessionId: number) =>
    api.get(`/api/v1/design-sessions/${sessionId}`),
};

// ─── Autotriage Pipeline ───

export const triageConfigAPI = {
  getConfig: () => api.get('/api/v1/triage-config/'),
  updateConfig: (data: {
    auto_triage_enabled?: boolean;
    auto_fix_enabled?: boolean;
    auto_deploy_enabled?: boolean;
    max_auto_approve_risk?: string;
    max_auto_fix_effort?: string;
    blocked_areas?: string[];
  }) => api.put('/api/v1/triage-config/', data),
};

export const autotriagePipelineAPI = {
  getStatus: (feedbackId: number) => api.get(`/api/v1/autotriage/${feedbackId}`),
};

// ─── Platform Admin APIs ───

export const adminUsersAPI = {
  list: (params?: { search?: string; limit?: number; offset?: number }) =>
    api.get('/api/v1/admin-users/', { params }),
  get: (userId: number) => api.get(`/api/v1/admin-users/${userId}`),
  update: (userId: number, data: Record<string, any>) =>
    api.put(`/api/v1/admin-users/${userId}`, data),
  stats: () => api.get('/api/v1/admin-users/stats'),
  impersonate: (userId: number) =>
    api.post(`/api/v1/admin-users/${userId}/impersonate`),
};

export const adminOrgsAPI = {
  list: (params?: { search?: string; limit?: number; offset?: number }) =>
    api.get('/api/v1/admin-orgs/', { params }),
  get: (orgId: number) => api.get(`/api/v1/admin-orgs/${orgId}`),
  update: (orgId: number, data: Record<string, any>) =>
    api.put(`/api/v1/admin-orgs/${orgId}`, data),
  addTokens: (orgId: number, data: { amount: number; description?: string }) =>
    api.post(`/api/v1/admin-orgs/${orgId}/tokens`, data),
};

export const adminSystemAPI = {
  health: () => api.get('/api/v1/admin-system/health'),
  jobs: () => api.get('/api/v1/admin-system/jobs'),
  triggerJob: (jobId: string) => api.post(`/api/v1/admin-system/jobs/${jobId}/trigger`),
};

// ─── Interaction Map ───

export const interactionMapAPI = {
  scan: (siteId: number, mode?: 'source' | 'live' | 'combined') =>
    api.post(`/api/v1/interaction-map/${siteId}/scan${mode ? `?mode=${mode}` : ''}`),
  getLatest: (siteId: number) =>
    api.get(`/api/v1/interaction-map/${siteId}`),
  getIssues: (siteId: number, params?: { severity?: string; issue_type?: string; resolved?: boolean }) =>
    api.get(`/api/v1/interaction-map/${siteId}/issues`, { params }),
  resolveIssue: (siteId: number, issueId: number) =>
    api.post(`/api/v1/interaction-map/${siteId}/issues/${issueId}/resolve`),
  createBacklog: (siteId: number, issueId: number) =>
    api.post(`/api/v1/interaction-map/${siteId}/issues/${issueId}/create-backlog`),
  createDirective: (siteId: number, issueId: number) =>
    api.post(`/api/v1/interaction-map/${siteId}/issues/${issueId}/create-directive`),
  listFlows: (siteId: number) =>
    api.get(`/api/v1/interaction-map/${siteId}/flows`),
  createFlow: (siteId: number, data: { name: string; description?: string; steps: any[]; is_critical?: boolean }) =>
    api.post(`/api/v1/interaction-map/${siteId}/flows`, data),
  updateFlow: (siteId: number, flowId: number, data: { name: string; description?: string; steps: any[]; is_critical?: boolean }) =>
    api.put(`/api/v1/interaction-map/${siteId}/flows/${flowId}`, data),
  deleteFlow: (siteId: number, flowId: number) =>
    api.delete(`/api/v1/interaction-map/${siteId}/flows/${flowId}`),
  validateFlow: (siteId: number, flowId: number) =>
    api.post(`/api/v1/interaction-map/${siteId}/flows/${flowId}/validate`),
  checkDeploy: (siteId: number) =>
    api.post(`/api/v1/interaction-map/${siteId}/check-deploy`),
};

export default api;
